import type { AlertRow, AlertsData, OpenFlag } from '../../api/types.js';

/**
 * The Alerts page's filtering, sorting and flag age — pure, so it is tested without rendering
 * (spec 008, R-14; FR-AL12, FR-AL18, FR-AL21–AL24).
 *
 * ⚠ **Nothing here decides who is on alert.** The server's `openAlerts` did that, once, for the page
 * and the emails alike (FR-AL60). This module only narrows and orders what it sent, which is why the
 * unfiltered count (`data.total`) is never recomputed here: the nav badge, the bell and the
 * unfiltered banner read that one number (FR-AL03).
 */

export const TIMELINES = ['all', '1D', '1M', '2M', '3M', '6M', '1Y'] as const;
export type Timeline = (typeof TIMELINES)[number];
export const TIMELINE_DAYS: Record<Exclude<Timeline, 'all'>, number> = {
  '1D': 1,
  '1M': 30,
  '2M': 60,
  '3M': 90,
  '6M': 180,
  '1Y': 365,
};

export type OfferingFilter = 'all' | 'nursing' | 'allied';

export interface AlertFilters {
  /** A `leaderKey`, or `all`. */
  leader: string;
  /** A manager id as a string, or `all` — strings so the filters persist as one enum record. */
  manager: string;
  /** An audit id, or `all`. */
  audit: string;
  offering: OfferingFilter;
  timeline: Timeline;
}

export const DEFAULT_ALERT_FILTERS: AlertFilters = {
  leader: 'all',
  manager: 'all',
  audit: 'all',
  offering: 'all',
  timeline: 'all',
};

// ---- Dates -----------------------------------------------------------------------------------

const DAY_MS = 86_400_000;

/**
 * Today's date in NEW YORK — the audit's own calendar (it runs at 06:00 Eastern and its due rule is
 * 07:30 Eastern, AU-18). A reader in India at 08:00 is still on the previous New York day, and
 * counting an age from their local date would read a flag from the newest run as "2d ago".
 */
export function newYorkToday(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
}

/** "Today", "Yesterday", "12d ago" — and "8d+ ago" where history ran out (FR-AL22, FR-AL23). */
export function ageLabel(days: number, lowerBound: boolean): string {
  if (lowerBound) return `${Math.max(days, 0)}d+ ago`;
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
}

// ---- The row's Flagged date (FR-AL22) --------------------------------------------------------

export interface FlaggedDate {
  since: string;
  lowerBound: boolean;
}

/** The flags the Audit-type filter selects: every flag on `all`. */
export function selectedFlags(row: AlertRow, audit: string): OpenFlag[] {
  return audit === 'all' ? row.flags : row.flags.filter((f) => f.auditId === audit);
}

/**
 * The EARLIEST start among the selected flags. ⚠ A lower bound wins a tie and propagates: if the
 * earliest start is "Oct 12 or earlier", the row's date is too — reporting it as exact would claim a
 * history the page never read.
 */
export function flaggedDate(row: AlertRow, audit: string): FlaggedDate | null {
  let best: FlaggedDate | null = null;
  for (const f of selectedFlags(row, audit)) {
    if (!best || f.since < best.since || (f.since === best.since && f.sinceIsLowerBound)) {
      best = { since: f.since, lowerBound: f.sinceIsLowerBound };
    }
  }
  return best;
}

// ---- Options -----------------------------------------------------------------------------------

/** Managers in the chosen leader's division, by name (FR-AL12). `all` ⇒ every manager. */
export function managerOptions(data: AlertsData, leader: string): AlertsData['managers'] {
  const allowed =
    leader === 'all' ? null : new Set(data.leaders.find((l) => l.leaderKey === leader)?.managerIds ?? []);
  return data.managers
    .filter((m) => !allowed || allowed.has(m.managerId))
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '') || a.managerId - b.managerId);
}

/**
 * A Timeline window the checked history cannot answer is disabled (FR-AL24). `1D` and `All` always
 * can: `1D` asks only about the newest run.
 */
export function timelineUnavailable(timeline: Timeline, reachableDays: number): boolean {
  if (timeline === 'all' || timeline === '1D') return false;
  return TIMELINE_DAYS[timeline] >= reachableDays;
}

// ---- The visible rows --------------------------------------------------------------------------

export function visibleRows(data: AlertsData, filters: AlertFilters, today: string): AlertRow[] {
  const leaderManagers =
    filters.leader === 'all'
      ? null
      : new Set(data.leaders.find((l) => l.leaderKey === filters.leader)?.managerIds ?? []);
  const newestRun = data.clock.latestRunDate;

  const kept = data.rows.filter((row) => {
    // A recruiter whose manager reports to two leaders matches either (FR-AL12, FR-D05).
    if (leaderManagers && !leaderManagers.has(row.managerId)) return false;
    if (filters.manager !== 'all' && String(row.managerId) !== filters.manager) return false;
    if (filters.offering !== 'all' && row.offering !== filters.offering) return false;
    const date = flaggedDate(row, filters.audit);
    if (!date) return false; // the Audit-type filter: no chip of that audit
    if (filters.timeline === '1D') return date.since === newestRun;
    if (filters.timeline !== 'all' && daysBetween(date.since, today) > TIMELINE_DAYS[filters.timeline]) {
      return false;
    }
    return true;
  });

  // FR-AL18: most chips, then most recently flagged, then name. Re-sorted here because the Audit-type
  // filter changes each row's Flagged date.
  const since = (r: AlertRow) => flaggedDate(r, filters.audit)?.since ?? '';
  return kept.sort(
    (a, b) =>
      b.flags.length - a.flags.length ||
      since(b).localeCompare(since(a)) ||
      (a.name ?? '').localeCompare(b.name ?? '') ||
      a.recruiterId - b.recruiterId,
  );
}

/** The chip's face: the audit and its threshold, or the service's name for an unconfirmed rule. */
export function chipLabel(data: AlertsData, auditId: string): string {
  const audit = data.audits.find((a) => a.id === auditId);
  return audit?.alertLabel ?? audit?.name ?? auditId;
}
